import { useEffect, useState } from 'react';

interface ReadPersistedValueResult {
  value: string | null;
}

function readPersistedValue(key: string): ReadPersistedValueResult {
  if (typeof window === 'undefined') return { value: null };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { value: null };
    return { value: raw };
  } catch {
    return { value: null };
  }
}

function writePersistedValue(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // If the browser blocks storage, we degrade to in-memory-only state.
  }
}

export function usePersistedSetting(key: string, fallback: string): [string, (next: string) => void] {
  const [value, setValue] = useState<string>(() => {
    const raw = readPersistedValue(key).value;
    return raw === null || raw.length === 0 ? fallback : raw;
  });

  useEffect(() => {
    writePersistedValue(key, value);
  }, [key, value]);

  const setNext = (next: string): void => {
    setValue(next);
  };

  return [value, setNext];
}
